import * as React from 'react'
import { forwardRef, ReactElement } from 'react'
import { Text, TextProps, Tooltip } from '@fluentui/react-components'
import { RichText } from './RichText'

export type TooltippedTextProps = TextProps & {
    /** Tooltip content; a blank string, null or undefined all mean "no tooltip". */
    tooltip?: string | null
    /** Renders a string tooltip as markdown through `RichText` instead of as plain text. */
    richTooltip?: boolean
}

/**
 * A Text that only shows a tooltip when there is actually something to show. Wrapping a Text in a
 * Tooltip yourself gives an empty tooltip bubble as soon as the content turns out to be blank.
 */
const TooltippedText = forwardRef<HTMLElement, TooltippedTextProps>(function TooltippedText(
    { tooltip, richTooltip, ...textProps },
    ref,
): ReactElement {
    const text = <Text {...textProps} ref={ref} />

    // A string tooltip of only whitespace says as little as an empty one, so it counts as absent.
    // Any other slot value (an element, a slot object) is taken at face value.
    const hasTooltip = typeof tooltip === 'string' ? tooltip.trim().length > 0 : !!tooltip
    if (!hasTooltip) {
        return text
    }

    // RichText mounts a tiptap editor, so it is only created once the tooltip has content to render.
    const content = richTooltip && typeof tooltip === 'string' ? <RichText content={tooltip} /> : tooltip

    return (
        <Tooltip content={content} relationship={'description'} withArrow>
            {text}
        </Tooltip>
    )
})

export default TooltippedText
